import IBAN from 'iban';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { User } from '~/types/user';

import { HttpBusinessConflictError } from '~/api/errors';
import { useUpdateFinancialInfo } from '~/api/user/useUpdateFinancialInfo';
import { Button, ErrorMessage } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { EditFinancialInfoForm as TEditFinancialInfoForm } from './EditFinancialInfoForm.types';

import { columnsStyle, formStyle, ibanFieldStyle, ibanRowStyle } from './EditFinancialInfoForm.css';

type Props = {
  onSuccess?: () => void;
  user: User;
};

const isForeignIbanError = (error: unknown): error is HttpBusinessConflictError => {
  return error instanceof HttpBusinessConflictError && error.errorCode === 'ForeignIban';
};

export const EditFinancialInfoForm = ({ onSuccess, user }: Props) => {
  const mutation = useUpdateFinancialInfo();

  const { clearErrors, control, handleSubmit, reset, setValue } = useForm<TEditFinancialInfoForm>({
    defaultValues: {
      bank: user.financial.bank,
      bic: user.financial.bic,
      iban: user.financial.iban,
    },
  });

  const { t } = useTranslation(['common', 'settings', 'validation']);

  const hasForeignIbanError = mutation.isError && isForeignIbanError(mutation.error);

  const submitFinancialInfo = async (data: TEditFinancialInfoForm) => {
    const { financial } = await mutation.mutateAsync({ financial: hasForeignIbanError ? data : { iban: data.iban, bank: '', bic: '' } });

    reset({ bank: financial.bank, bic: financial.bic, iban: financial.iban });
    onSuccess?.();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await submitFinancialInfo(data);
      toast.success({ description: t('settings:alerts.successUpdated') });
    } catch (error) {
      if (isForeignIbanError(error)) return;

      toast.danger({ description: t('settings:profile.financial.error.general') });
    }
  });

  return (
    <Form className={formStyle} onSubmit={onSubmit}>
      <Controller
        control={control}
        name="iban"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
          <Field.Root disabled={mutation.isPending} invalid={invalid} name={name}>
            <Field.Label>{t('settings:profile.financial.iban')}</Field.Label>

            <div className={ibanRowStyle}>
              <Input
                className={ibanFieldStyle}
                onBlur={onBlur}
                onChange={(newValue) => {
                  onChange(newValue);

                  mutation.reset();
                  setValue('bank', '');
                  setValue('bic', '');
                  clearErrors('bank');
                  clearErrors('bic');
                }}
                placeholder={t('settings:profile.financial.iban')}
                ref={ref}
                value={value}
              />

              <Button emphasis="secondary" icon="CheckProgress" loading={mutation.isPending} type="submit">
                {hasForeignIbanError ? t('common:actions.save') : t('settings:profile.financial.check')}
              </Button>
            </div>

            <Field.Error match={!!error}>{error?.message}</Field.Error>
            {hasForeignIbanError && <ErrorMessage>{t('settings:profile.financial.error.foreignIban')}</ErrorMessage>}
          </Field.Root>
        )}
        rules={{
          required: { value: true, message: t('validation:required') },
          validate: {
            notBlank: (value) => isNotBlank(value) || t('validation:required'),
            iban: (value) => IBAN.isValid(value) || t('validation:iban'),
          },
        }}
      />

      <div className={columnsStyle}>
        <Controller
          control={control}
          name="bank"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
            <Field.Root disabled={!hasForeignIbanError || mutation.isPending} invalid={invalid} name={name}>
              <Field.Label>{t('settings:profile.financial.bank')}</Field.Label>

              <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{ required: hasForeignIbanError && t('validation:required') }}
        />

        <Controller
          control={control}
          name="bic"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
            <Field.Root disabled={!hasForeignIbanError || mutation.isPending} invalid={invalid} name={name}>
              <Field.Label>{t('settings:profile.financial.bic')}</Field.Label>

              <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{ required: hasForeignIbanError && t('validation:required') }}
        />
      </div>
    </Form>
  );
};
